import * as angular from 'angular';

import { ChartDirectiveBase } from './ChartDirectiveBase';
import { Margins, ChartOptions, ChartConfigurationOption, IMargins, IChartOptions } from '../../../models';
import { SvgManager } from '../../../charting/SvgManager';
import { LineChart, IAreaConfiguration, IPaneConfiguration } from '../../../charting';
import { IDynamicCompileService } from '../../services/dynamicCompileService';
import { lineChartLeftMargin } from '../../../constants';
import { wtTrendSelection } from '../../../../common/events';

export interface ILineChartDirective extends angular.IDirective {
}

export interface ILineChartDirectiveScope extends angular.IScope {
    areaSeries: any;
    chartData: any;
    clicked: any;
    editedArea: IAreaConfiguration;
    hovered: any;
    id: string;
    isZoomed: boolean;
    legendHovered: (d, i) => void;
    options: ChartConfigurationOption;
    ratio?: number;
    series: any;
    zoomControl: any;
}

export class LineChartDirective extends ChartDirectiveBase implements ILineChartDirective {
    static directiveId = 'wtLineChart';

    constructor(private _dynamicCompileService: IDynamicCompileService) {
        super();
        this.scope = {
            areaSeries: '=',
            chartData: '=',
            clicked: '=?',
            editedArea: '=?',
            hovered: '=?',
            id: '@',
            isZoomed: '=?',
            legendHovered: '=?',
            options: '=',
            ratio: '<?',
            series: '=',
            zoomControl: '=?',
        };
        this.link.bind(this);
    }

    link = (scope: ILineChartDirectiveScope, element: angular.IAugmentedJQuery, attrs, _controller) => {
        const containerName = attrs.id;
        element.attr('id', containerName);
        const margins = new Margins(0, 0, 0, 0);
        const lineSvgManager = new SvgManager(containerName, margins);
        const options = new ChartOptions(lineChartLeftMargin, 10, 0, lineChartLeftMargin);
        const lineChart = new LineChart(options, lineSvgManager);

        scope.$watch(() => scope.options, chartOptions => {
            this.bindOptions(chartOptions, lineChart);
            if (chartOptions) {
                this.watchOptions(scope, chartOptions, lineChart);
                if (chartOptions.display) {
                    this.watchDisplayOptions(scope, chartOptions, lineChart);
                }
                if (chartOptions.panes) {
                    this.watchPanes(scope, chartOptions, lineChart, element);
                }
            }
        });

        this.initZoomControl(scope, lineChart);

        scope.$watchCollection(() => scope.chartData, () => {
            if (lineChart) {
                lineChart.update(scope.chartData);
            }
        });

        scope.$watch(() => scope.series, () => {
            if (scope.series) {
                lineChart.updateSeries(scope.series);
                // if series has changed, we should redraw data
                lineChart.update(scope.chartData);
                this._dynamicCompileService.compileGroup(element, scope, () => {
                    // vodoo magic to fix click handler after recompile
                    lineChart.fixLegend();
                });
            }
        }, true);

        scope.$watch(() => scope.hovered, () => {
            if (scope.hovered) {
                lineChart.highlight(scope.hovered.data, d => d.timeStamp, 'hovered', scope.hovered.classed);
            }
        });

        scope.$watch(() => scope.clicked, () => {
            if (scope.clicked) {
                lineChart.highlight(scope.clicked.data, d => d.timeStamp, 'clicked', scope.clicked.classed);
            }
        });

        scope.$watch(() => scope.legendHovered, (newValue) => {
            if (newValue) {
                lineChart.legend().on('hover', (d, i) => newValue(d, i));
            }
        });

        const dispatch = lineChart.dispatch();

        dispatch.on('lineChartZoom', () => {
            if (!scope.$$phase && !scope.$root.$$phase) {
                scope.$apply();
            }
        });

        dispatch.on('selection', d => {
            scope.$emit(wtTrendSelection, d);
            lineChart.resetSelection();
        });

        lineChart.onCrosshair((date: Date) => {
            scope.options.crosshairDate = date;
            if (!scope.$$phase && !scope.$root.$$phase) {
                scope.$apply();
            }
        });

        lineChart.legend().on('hover', d => {
            lineChart.highlight(scope.hovered.data, d => d.timeStamp, 'hovered', scope.hovered.classed);
        });

        lineChart.legend().on('click', d => {
            lineChart.setSerieState(d ? d._identifier : undefined, true);
        });

        scope.$watch(() => scope.ratio, () => {
            lineChart.ratio(scope.ratio);
        });
    }

    private initZoomControl(scope: ILineChartDirectiveScope, lineChart: LineChart) {
        if (!scope.zoomControl) {
            scope.zoomControl = {};
        }

        scope.zoomControl.reset = () => {
            lineChart.resetZoom();
        };

        scope.zoomControl.resetSelection = () => {
            lineChart.resetSelection();
        };

        scope.zoomControl.pan = () => {
            lineChart.pan();
        };

        scope.zoomControl.select = () => {
            lineChart.select();
        };

        scope.zoomControl.isZoomed = () => {
            return lineChart.isZoomed();
        };

        scope.zoomControl.mode = () => {
            return lineChart.mode();
        };
    }

    private watchPanes(scope: ILineChartDirectiveScope, chartOptions: ChartConfigurationOption, lineChart: LineChart, element: angular.IAugmentedJQuery) {
        scope.$watch(
            () => chartOptions.panes,
            (newValue: IPaneConfiguration[]) => {
                if (lineChart) {
                    lineChart.panes(newValue);
                    if (!!newValue && newValue.filter) {
                        const fakeSeries = newValue
                            .filter(d => !!d.titleAttributes)
                            .map(d => { return { attributes: d.titleAttributes }; });
                        if (fakeSeries.length) {
                            this._dynamicCompileService.compileGroup(element, scope, null, fakeSeries);
                        }
                    }
                }
            },
            true
        );
    }

    private watchDisplayOptions(scope: ILineChartDirectiveScope, chartOptions: ChartConfigurationOption, lineChart: LineChart) {
        scope.$watch(
            () => chartOptions.display.yMin,
            () => lineChart.yMin(chartOptions.display.yMin)
        );

        scope.$watch(
            () => chartOptions.display.yMax,
            () => lineChart.yMax(chartOptions.display.yMax)
        );

        scope.$watch(
            () => chartOptions.display.yAxisFormat,
            () => lineChart.yAxisFormat(chartOptions.display.yAxisFormat)
        );

        scope.$watch(
            () => chartOptions.display.xMin,
            () => lineChart.xMin(chartOptions.display.xMin)
        );

        scope.$watch(
            () => chartOptions.display.xMax,
            () => lineChart.xMax(chartOptions.display.xMax)
        );

        scope.$watch(
            () => chartOptions.display.xRange,
            () => lineChart.xRange(chartOptions.display.xRange)
        );
    }

    private watchOptions(scope: ILineChartDirectiveScope, chartOptions: ChartConfigurationOption, lineChart: LineChart) {
        scope.$watch(
            () => chartOptions.title,
            () => lineChart.titleText(chartOptions.title)
        );

        scope.$watch(
            () => chartOptions.isTitleVisible,
            () => lineChart.isTitleVisible(chartOptions.isTitleVisible)
        );

        scope.$watch(
            () => chartOptions.isLegendVisible,
            () => lineChart.isLegendVisible(chartOptions.isLegendVisible)
        );

        scope.$watch(
            () => chartOptions.isHorizontalGridlinesVisible,
            () => lineChart.isHorizontalGridlinesVisible(chartOptions.isHorizontalGridlinesVisible)
        );

        scope.$watch(
            () => chartOptions.showXAxis,
            () => {
                if (chartOptions.showXAxis === undefined || chartOptions.showXAxis === null) {
                    chartOptions.showXAxis = true;
                }
                lineChart.isXAxisVisible(chartOptions.showXAxis);
                lineChart.update(scope.chartData);
            }
        );

        scope.$watch(
            () => chartOptions.showYAxis,
            () => {
                if (chartOptions.showYAxis === undefined || chartOptions.showYAxis === null) {
                    chartOptions.showYAxis = true;
                }
                lineChart.isYAxisVisible(chartOptions.showYAxis);
                lineChart.update(scope.chartData);
            }
        );

        scope.$watch(
            () => chartOptions.horizontalLines,
            () => lineChart.horizontalLines(chartOptions.horizontalLines)
        );

        scope.$watch(
            () => chartOptions.hasAbsolutePanesHeight,
            () => lineChart.hasAbsolutePanesHeight(chartOptions.hasAbsolutePanesHeight)
        );

        scope.$watch(
            () => chartOptions.crosshairDate,
            () => lineChart.updateCrosshair(chartOptions.crosshairDate)
        );
    }

    private bindOptions(chartOptions: ChartConfigurationOption, lineChart: LineChart) {
        if (chartOptions) {
            lineChart.titleText(chartOptions.title);
            lineChart.isTitleVisible(chartOptions.isTitleVisible);
            lineChart.isLegendVisible(chartOptions.isLegendVisible);
            lineChart.isHorizontalGridlinesVisible(chartOptions.isHorizontalGridlinesVisible);
            lineChart.isXAxisVisible(chartOptions.showXAxis);
            lineChart.isYAxisVisible(chartOptions.showYAxis);
            lineChart.horizontalLines(chartOptions.horizontalLines);
            lineChart.hasAbsolutePanesHeight(chartOptions.hasAbsolutePanesHeight);
            lineChart.updateCrosshair(chartOptions.crosshairDate);
            if (chartOptions.display) {
                lineChart.yMin(chartOptions.display.yMin);
                lineChart.yMax(chartOptions.display.yMax);
                lineChart.yAxisFormat(chartOptions.display.yAxisFormat);
                lineChart.xMin(chartOptions.display.xMin);
                lineChart.xMax(chartOptions.display.xMax);
            }
            lineChart.panes(chartOptions.panes);
        }
    }


}